(() => {
  if (customElements.get('ht-live-search')) return;
  class HairTempleLiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.input = this.querySelector('input[name="q"]');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('[data-search-panel]');
      this.content = this.querySelector('[data-search-content]');
      this.status = this.querySelector('[data-search-status]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.cache = new Map();
      this.requestId = 0;
      this.events = new AbortController();
      const on = (node, type, listener) => node.addEventListener(type, listener, { signal: this.events.signal });
      on(this.input, 'input', event => { if (!event.isComposing) this.queue(); });
      on(this.input, 'compositionend', () => this.queue());
      on(this.input, 'focus', () => this.queue());
      on(this.input, 'keydown', event => this.onKeydown(event));
      on(this.form, 'submit', event => { if (!this.input.value.trim()) event.preventDefault(); });
      on(this.clearButton, 'click', () => { this.input.value = ''; this.close(); this.clearButton.hidden = true; this.input.focus(); });
      on(this.querySelector('[data-search-close]'), 'click', () => { this.input.focus(); this.close(); });
      on(document, 'pointerdown', event => { if (!this.contains(event.target)) this.close(); });
      on(this, 'focusout', event => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        setTimeout(() => { if (this.isConnected && !this.contains(document.activeElement)) this.close(); }, 0);
      });
      on(window, 'resize', () => this.fitPanel());
      if (window.visualViewport) on(window.visualViewport, 'resize', () => this.fitPanel());
      this.clearButton.hidden = !this.input.value;
    }

    disconnectedCallback() {
      this.cancel();
      this.events?.abort();
      this.events = null;
    }

    cancel() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.requestId++;
    }

    clearSelection() {
      this.input.removeAttribute('aria-activedescendant');
      this.content.querySelectorAll('[aria-selected="true"]').forEach(option => option.setAttribute('aria-selected', 'false'));
    }

    queue() {
      this.cancel();
      const query = this.input.value.trim();
      this.clearButton.hidden = !this.input.value;
      this.clearSelection();
      if (query.length < 2) { this.close(); return; }
      this.content.replaceChildren();
      const message = document.createElement('p');
      message.className = 'ht-search-message ht-search-loading';
      message.textContent = 'Buscando productos…';
      this.content.append(message);
      const pendingList = document.createElement('ul');
      pendingList.id = this.input.getAttribute('aria-controls');
      pendingList.className = 'ht-search-list';
      pendingList.setAttribute('role', 'listbox');
      pendingList.setAttribute('aria-label', 'Productos sugeridos');
      this.content.append(pendingList);
      this.content.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Buscando productos';
      this.open();
      const id = this.requestId;
      this.timer = setTimeout(() => this.suggest(query, id), 220);
    }

    async suggest(query, id) {
      let searchEvent;
      const SearchUpdateEvent = window.StandardEvents?.SearchUpdateEvent;
      if (SearchUpdateEvent) {
        searchEvent = SearchUpdateEvent.createPromise();
        this.dispatchEvent(new SearchUpdateEvent({ search: { query }, promise: searchEvent.promise }));
      }
      try {
        let markup = this.cache.get(query);
        if (!markup) {
          const url = new URL(this.dataset.endpoint, window.location.origin);
          url.search = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last', section_id: 'ht-predictive-search' });
          this.controller = new AbortController();
          const response = await fetch(url, { signal: this.controller.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Search unavailable');
          markup = await response.text();
        }
        if (id !== this.requestId || query !== this.input.value.trim()) { searchEvent?.resolve({ totalCount: 0 }); return; }
        const result = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-ht-search-results]');
        if (!result) throw new Error('Missing search results');
        const list = result.querySelector('[role="listbox"]');
        list.id = this.input.getAttribute('aria-controls');
        result.querySelectorAll('[role="option"]').forEach((option, index) => { option.id = `${list.id}-${index}`; });
        const count = Number(result.dataset.resultCount) || 0;
        this.content.replaceChildren(result);
        this.content.setAttribute('aria-busy', 'false');
        this.status.textContent = count ? `${count === 1 ? '1 producto sugerido' : `${count} productos sugeridos`}. Usa las flechas para elegir y pulsa Intro para abrir.` : 'No encontramos productos. Prueba otra búsqueda.';
        this.cache.set(query, markup);
        if (this.cache.size > 20) this.cache.delete(this.cache.keys().next().value);
        this.fitPanel();
        searchEvent?.resolve({ totalCount: count });
      } catch (error) {
        searchEvent?.resolve({ totalCount: 0 });
        if (error.name === 'AbortError' || id !== this.requestId) return;
        this.content.setAttribute('aria-busy', 'false');
        const message = document.createElement('p');
        message.className = 'ht-search-message';
        message.textContent = 'Puedes continuar en la búsqueda completa.';
        const link = document.createElement('a');
        const url = new URL(this.form.action);
        url.search = new URLSearchParams({ q: query, type: 'product', 'options[prefix]': 'last' });
        link.href = url;
        link.className = 'ht-search-all';
        link.textContent = `Buscar «${query}» →`;
        this.content.replaceChildren(message, link);
        this.input.setAttribute('aria-expanded', 'false');
        this.status.textContent = message.textContent;
      }
    }

    onKeydown(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
      if (event.key === 'Tab') { this.close(); return; }
      if (event.key === 'Enter') {
        const active = this.content.querySelector('[aria-selected="true"] a');
        if (!this.panel.hidden && active) { event.preventDefault(); active.click(); }
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      if (this.panel.hidden) { this.queue(); return; }
      const options = [...this.content.querySelectorAll('[role="option"]')];
      if (!options.length) return;
      let index = options.findIndex(option => option.getAttribute('aria-selected') === 'true');
      index = (index + (event.key === 'ArrowDown' ? 1 : index < 0 ? 0 : -1) + options.length) % options.length;
      this.clearSelection();
      options[index].setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', options[index].id);
      const panelBounds = this.panel.getBoundingClientRect();
      const optionBounds = options[index].getBoundingClientRect();
      const footer = this.content.querySelector('.ht-search-all-option');
      const visibleBottom = footer && options[index] !== footer ? Math.min(panelBounds.bottom, footer.getBoundingClientRect().top) : panelBounds.bottom;
      if (optionBounds.bottom > visibleBottom) this.panel.scrollTop += optionBounds.bottom - visibleBottom;
      else if (optionBounds.top < panelBounds.top) this.panel.scrollTop -= panelBounds.top - optionBounds.top;
    }

    fitPanel() {
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
      this.style.setProperty('--ht-search-height', `${Math.max(120, bottom - this.form.getBoundingClientRect().bottom - 24)}px`);
    }

    open() {
      document.querySelectorAll('[data-ht-menu][open], [data-ht-dropdown][open]').forEach(menu => { menu.open = false; });
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.fitPanel();
    }

    close() {
      this.cancel();
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.content.setAttribute('aria-busy', 'false');
      this.clearSelection();
      this.status.textContent = '';
    }
  }
  customElements.define('ht-live-search', HairTempleLiveSearch);
})();
